"use client";

import { createContext, useContext, useEffect, useState } from "react";
import Image from "next/image";
import { MediaController } from "media-chrome/react";
import { MediaProvider } from "media-chrome/react/media-store";

import { cn } from "@marketing-sites/utils/cn";

import type { MediaPlayerContextType } from "./media-player.types";
import "./media-player.styles.css";

const MediaPlayerContext = createContext<MediaPlayerContextType>({
  video: {
    url: "",
    id: "",
    title: "",
  },
  overlay: {
    showWhenPaused: false,
  },
});

export const useMediaPlayerContext = () => useContext(MediaPlayerContext);

type ContextProps = {
  children: React.ReactNode;
} & MediaPlayerContextType;

export const MediaPlayerContextProvider = ({
  children,
  video,
  overlay,
  previewVideo,
}: ContextProps) => {
  return (
    <MediaPlayerContext.Provider value={{ video, overlay, previewVideo }}>
      <MediaProvider>{children}</MediaProvider>
    </MediaPlayerContext.Provider>
  );
};

/**
 * SSR-safe poster image rendered before the media player mounts.
 *
 * MediaController requires browser APIs so it's gated behind an isMounted check,
 * rendering nothing on the server. The video element lives inside that gate, so
 * this image is what paints on first load and is the LCP candidate until the
 * player mounts on top.
 *
 * Above the fold (`priority`), this is a plain <img> at the raw asset URL — not
 * next/image — so its URL exactly matches the mounted video's native `poster`
 * attribute (a raw string Next never rewrites). next/image can't produce that
 * match: optimised forks the URL to `/_next/image?...`, and `unoptimized`
 * appends `?dpl=<deploymentId>` in production while the raw `poster` string
 * carries none. Sharing one URL is what makes the real LCP resource — the
 * on-top native poster, requested client-side post-mount — count as preloaded
 * and discoverable: a plain <img src> in the SSR HTML is itself scanner-
 * discoverable, and `fetchPriority="high"` marks it, with no duplicate download.
 *
 * Below the fold, next/image (optimised + lazy) is fine — those posters aren't
 * LCP and never need to match a native poster in the initial document.
 */
const DEFAULT_POSTER_SIZES = "(max-width: 768px) 100vw, 800px";

const Poster = ({
  src,
  alt,
  sizes = DEFAULT_POSTER_SIZES,
  priority = false,
}: {
  src: string;
  alt: string;
  sizes?: string;
  priority?: boolean;
}) =>
  priority ? (
    // eslint-disable-next-line @next/next/no-img-element -- raw URL must match the native <video poster> (see above); next/image would fork it
    <img
      src={src}
      alt={alt}
      // Matches the video's `crossOrigin` — Chrome keys its cache on CORS mode, so
      // a no-cors <img> and the cors-mode native poster would each download the
      // same URL in full. Load-bearing, not decorative.
      crossOrigin="anonymous"
      fetchPriority="high"
      decoding="async"
      className="z-1 absolute inset-0 h-full w-full bg-black/20 object-cover"
    />
  ) : (
    <Image
      src={src}
      alt={alt}
      fill
      sizes={sizes}
      className="z-1 bg-black/20 object-cover"
    />
  );

export const MediaPlayerRoot = ({
  className,
  children,
  priority = false,
}: {
  className?: string;
  children: React.ReactNode;
  /**
   * Eagerly load the poster (`next/image` `priority`: eager + fetchpriority=high +
   * preload). Only set this when the player is above the fold — leave it off (the
   * default) for below-the-fold players so their posters stay lazy.
   */
  priority?: boolean;
}) => {
  const { video } = useContext(MediaPlayerContext);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <>
      {video?.poster && (
        <Poster
          src={video.poster}
          alt={video?.title ?? ""}
          sizes={video?.posterSizes}
          priority={priority}
        />
      )}
      {mounted && (
        <MediaController
          id={`mediacontroller-${video?.id}`}
          data-media-player
          className={cn(
            className,
            "group/mediaplayercontroller z-2 absolute inset-0 isolate overflow-hidden",
          )}
        >
          {children}
        </MediaController>
      )}
    </>
  );
};
